import { invariant } from '@epic-web/invariant';
import {
  getCellRendererFunc,
  isValidQuotePrice,
  SideEnum,
  type CareOrder,
  type ExpressedOutcomeEnum,
  type PricingModeEnum,
  type Quote,
  type QuoteMarket,
  type QuoteStatusEnum,
  type TreeRow,
  type User,
} from '@talos/kyoko';
import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { getMarketQty, type QuoteMarketWithBestBidOffer, type RfqBestQuoteBy } from '../../../utils/rfq';
import { getQuoteMarketsForQuoteRow, getQuoteOpenButtonDisabledReason, getQuoteTradeSideDisabledReason } from './utils';

export type QuotesBlotterRow = SweepGroupIDRow | ParentRFQIDRow | InitiatingFromRow | QuoteRow | QuoteMarketRow;

export function isPricingRow(row: unknown): row is QuoteRow | QuoteMarketRow {
  return row instanceof QuoteRow || row instanceof QuoteMarketRow;
}

interface RowWithPrices {
  Symbol: string;
  SubmitTime: string;
  OrderQty: string | undefined;
  User: string | undefined;
  bidQty: string | undefined;
  offerQty: string | undefined;
  sizeCountering: string | undefined;
  Currency: string | undefined;
  units: string | undefined;
  Side: SideEnum | undefined;
  QuoteStatus: QuoteStatusEnum;
  Text: string | undefined;
  ExpressedOutcome: ExpressedOutcomeEnum | undefined;
  PricingMode: PricingModeEnum | undefined;
  AmountCurrency: string | undefined;
  bid: QuoteMarketWithBestBidOffer | undefined;
  offer: QuoteMarketWithBestBidOffer | undefined;
  bidDisplayPx: string | undefined;
  offerDisplayPx: string | undefined;
}

export class QuoteRow implements TreeRow, RowWithPrices {
  quote: Quote;
  dataPath: string[];

  Symbol: string;
  SubmitTime: string;
  OrderQty: string | undefined;
  User: string | undefined;
  bidQty: string | undefined;
  offerQty: string | undefined;
  sizeCountering: string | undefined;
  Currency: string | undefined;
  units: string | undefined;
  Side: SideEnum | undefined;

  Text: string | undefined;
  ExpressedOutcome: ExpressedOutcomeEnum | undefined;
  QuoteStatus: QuoteStatusEnum;
  PricingMode: PricingModeEnum | undefined;
  AmountCurrency: string | undefined;
  bid: QuoteMarketWithBestBidOffer | undefined;
  offer: QuoteMarketWithBestBidOffer | undefined;
  bidDisplayPx: string | undefined;
  offerDisplayPx: string | undefined;

  /** The care order's side, if a care order enrichment exists. This side "is law" and the opposite side should be disabled. */
  careOrderSide: SideEnum | undefined;

  /** The quote-level expiry time (EndTime) */
  expiryTime: string | undefined;

  constructor(dataPath: string[], quote: Quote, bestBy?: RfqBestQuoteBy) {
    this.quote = quote;

    this.SubmitTime = quote.SubmitTime;
    this.Symbol = quote.Symbol;
    this.OrderQty = quote.OrderQty;
    this.User = quote.User;
    this.Currency = quote.Currency;
    this.units = quote.units ?? quote.careOrder?.units;
    this.careOrderSide = quote.careOrder?.Side;
    this.Side = this.careOrderSide ?? quote.tradedSideOrSide;
    this.Text = quote.Text;
    this.ExpressedOutcome = quote.ExpressedOutcome;
    this.QuoteStatus = quote.QuoteStatus;
    this.PricingMode = quote.PricingMode;
    this.AmountCurrency = quote.AmountCurrency;
    this.expiryTime = quote.EndTime;

    const { bid, offer } = getQuoteMarketsForQuoteRow(quote, bestBy);
    this.bid = bid;
    this.offer = offer;
    this.sizeCountering = quote.NegotiationParameters?.SizeCountering;
    this.bidDisplayPx = isValidQuotePrice(bid?.BidPx, bid?.QuoteStatus, this.PricingMode) ? bid?.BidPx : undefined;
    this.offerDisplayPx = isValidQuotePrice(offer?.OfferPx, offer?.QuoteStatus, this.PricingMode)
      ? offer?.OfferPx
      : undefined;
    this.bidQty = getMarketQty(bid?.BidQty, quote.OrderQty, this.bidDisplayPx);
    this.offerQty = getMarketQty(offer?.OfferQty, quote.OrderQty, this.offerDisplayPx);

    this.dataPath = dataPath;
  }

  /** The column id of which column this grouping level is grouping on. Ties this level of grouping to a column. */
  static getGroupColumnID(): string {
    return 'quote.RFQID';
  }

  get groupColumnID() {
    return QuoteRow.getGroupColumnID();
  }

  /** A static function returning the data path component this class (level) represents */
  static getPathComponent(quote: Quote) {
    return quote.RFQID;
  }

  /** A static function to allow you to preview the row id for a given quote */
  static getRowID(dataPath: string[]): string {
    invariant(dataPath.length > 0, 'Unable to call QuoteRow.getRowID with dataPath parameter of length 0');
    return dataPath.at(-1)!; // our rowID is just our part of the data path, see getPathComponent above
  }

  get rowID() {
    return QuoteRow.getRowID(this.dataPath);
  }

  groupColumnValueGetter(params: ValueGetterParams<QuoteRow>) {
    return this.quote.RFQID;
  }

  groupColumnValueFormatter({ value, context }: ValueFormatterParams<QuoteRow, string>): string {
    return value ?? '';
  }

  groupColumnFilterValueGetter(params: ValueGetterParams<QuoteRow>) {
    return this.groupColumnValueGetter(params);
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<QuoteRow, string>) {
    const cellRendererFunc = getCellRendererFunc(params.api, QuoteRow.getGroupColumnID());

    if (cellRendererFunc) {
      return cellRendererFunc(params);
    }

    return params.valueFormatted ?? '';
  }
}

export class QuoteMarketRow implements TreeRow, RowWithPrices {
  parentQuote: Quote;
  quoteMarket: QuoteMarket;
  dataPath: string[];

  SubmitTime: string;
  Symbol: string;
  OrderQty: string | undefined;
  User: string | undefined;
  bidQty: string | undefined;
  offerQty: string | undefined;
  sizeCountering: string | undefined;
  Currency: string | undefined;
  units: string | undefined;
  Side: SideEnum | undefined;
  Text: string | undefined;
  ExpressedOutcome: ExpressedOutcomeEnum | undefined;
  QuoteStatus: QuoteStatusEnum;
  PricingMode: PricingModeEnum | undefined;
  AmountCurrency: string | undefined;
  bid: QuoteMarketWithBestBidOffer | undefined;
  offer: QuoteMarketWithBestBidOffer | undefined;
  bidDisplayPx: string | undefined;
  offerDisplayPx: string | undefined;

  /** The care order's side, propagated from the parent QuoteRow. */
  careOrderSide: SideEnum | undefined;

  /** The maker's response expiry time (ValidUntilTime) */
  expiryTime: string | undefined;

  constructor(dataPath: string[], quote: Quote, quoteMarket: QuoteMarketWithBestBidOffer) {
    this.parentQuote = quote;
    this.quoteMarket = quoteMarket;
    this.dataPath = dataPath;

    this.SubmitTime = quote.SubmitTime;
    this.Symbol = quote.Symbol;
    this.OrderQty = quote.OrderQty;
    this.User = quote.User;
    this.Currency = quote.Currency;
    this.units = undefined; // RowWithPrices requires units, but we only want to render it on the quote-level row

    // quote.Side is the submitted side. Quote.TradedSide is the TradedSide of the executed market, which isn't necessarily us.
    this.Side = quote.Side ?? quoteMarket.TradedSide;
    this.Text = quoteMarket.Text;
    this.ExpressedOutcome = quote.ExpressedOutcome;
    this.QuoteStatus = quoteMarket.QuoteStatus;
    this.PricingMode = quote.PricingMode;
    this.AmountCurrency = quote.AmountCurrency;
    this.bid = quoteMarket;
    this.offer = quoteMarket;
    this.sizeCountering = undefined;
    this.bidDisplayPx = isValidQuotePrice(quoteMarket?.BidPx, quoteMarket?.QuoteStatus, this.PricingMode)
      ? quoteMarket?.BidPx
      : undefined;
    this.offerDisplayPx = isValidQuotePrice(quoteMarket?.OfferPx, quoteMarket?.QuoteStatus, this.PricingMode)
      ? quoteMarket?.OfferPx
      : undefined;
    this.bidQty = getMarketQty(quoteMarket.BidQty, quote.OrderQty, this.bidDisplayPx);
    this.offerQty = getMarketQty(quoteMarket.OfferQty, quote.OrderQty, this.offerDisplayPx);
    this.careOrderSide = quote.careOrder?.Side;
    this.expiryTime = quoteMarket.ValidUntilTime;
  }

  /** The column id of which column this grouping level is grouping on. Ties this level of grouping to a column. */
  static getGroupColumnID(): string {
    return 'Market' satisfies keyof QuoteMarket;
  }

  get groupColumnID() {
    return QuoteMarketRow.getGroupColumnID();
  }

  /** A static function returning the data path component this class (level) represents */
  static getPathComponent(quote: Quote, quoteMarket: QuoteMarket) {
    return quoteMarket.Market;
  }

  /** A static function to allow you to preview the row id for a given quote */
  static getRowID(dataPath: string[]): string {
    // QuoteMarketRows are children of QuoteRows. The RowID here then becomes `Quote.RFQID-QuoteMarket.Market`
    return dataPath.join('-');
  }

  get rowID() {
    return QuoteMarketRow.getRowID(this.dataPath);
  }

  groupColumnValueGetter(params: ValueGetterParams<QuoteMarketRow>) {
    return this.quoteMarket.Market;
  }

  groupColumnValueFormatter({ value, context }: ValueFormatterParams<QuoteMarketRow, string>): string {
    if (!value) {
      return '';
    }

    return context.current.marketDisplayNameByName?.get(value) ?? value;
  }

  groupColumnFilterValueGetter(params: ValueGetterParams<QuoteMarketRow>) {
    return this.groupColumnValueGetter(params);
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<QuoteMarketRow, string>) {
    const cellRendererFunc = getCellRendererFunc(params.api, QuoteMarketRow.getGroupColumnID());

    if (cellRendererFunc) {
      return cellRendererFunc(params);
    }

    return params.valueFormatted ?? '';
  }
}

const UNASSIGNED_INITIATING_FROM = 'None';
const UNASSIGNED_PARENT_RFQ_ID = 'None';
const UNASSIGNED_SWEEP_GROUP_ID = 'None';

export class SweepGroupIDRow implements TreeRow {
  static readonly unassignedValue = UNASSIGNED_SWEEP_GROUP_ID;

  dataPath: string[];
  SweepGroupID: string;
  quotes: Quote[];
  isDisabled: boolean;
  isBuyDisabled: boolean;
  isSellDisabled: boolean;
  Symbol: string | undefined;
  OrderQty: string | undefined;
  Currency: string | undefined;
  User: string | undefined;
  SubmitTime?: string; // just needs to exist here for typing purposes on QuotesBlotterRow (its the default sorting key)
  Side = null; // Setting side to null tells the column that we should not render anything

  constructor(
    dataPath: string[],
    quotes: Quote[],
    user: User,
    isMobile = false,
    requireMobileUITraderRole = false,
    enableExecutingOtherUserNegotiatedRFQ = false
  ) {
    invariant(quotes.length > 0, 'Unable to create SweepGroupIDRow without any quotes');
    const representativeQuote = quotes.at(0);

    this.dataPath = dataPath;
    this.quotes = quotes;
    this.SweepGroupID = representativeQuote?.SweepGroupID ?? UNASSIGNED_SWEEP_GROUP_ID;
    this.Symbol = representativeQuote?.Symbol;
    this.OrderQty = representativeQuote?.OrderQty;
    this.Currency = representativeQuote?.Currency;
    this.User = representativeQuote?.User;
    this.SubmitTime = this.SweepGroupID === UNASSIGNED_SWEEP_GROUP_ID ? undefined : representativeQuote?.SubmitTime;

    const careOrderSide = this.associatedCareOrder?.Side;
    const hasTradableSide = (side: SideEnum) =>
      quotes.some(quote => {
        const { bid, offer } = getQuoteMarketsForQuoteRow(quote);
        const quoteMarket = side === SideEnum.Sell ? bid : offer;

        return (
          getQuoteTradeSideDisabledReason({
            quote,
            quoteMarket,
            side,
            user,
            careOrderSide,
            isMobile,
            requireMobileUITraderRole,
            enableExecutingOtherUserNegotiatedRFQ,
          }) == null
        );
      });

    this.isSellDisabled = this.SweepGroupID === UNASSIGNED_SWEEP_GROUP_ID || !hasTradableSide(SideEnum.Sell);
    this.isBuyDisabled = this.SweepGroupID === UNASSIGNED_SWEEP_GROUP_ID || !hasTradableSide(SideEnum.Buy);
    const hasOpenableQuote = quotes.some(
      quote => getQuoteOpenButtonDisabledReason({ quote, user, enableExecutingOtherUserNegotiatedRFQ }) == null
    );

    this.isDisabled =
      this.SweepGroupID === UNASSIGNED_SWEEP_GROUP_ID ||
      (!hasOpenableQuote && this.isBuyDisabled && this.isSellDisabled);
  }

  /**
   * Care-order enrichment shared by every quote in this sweep group.
   *
   * When any quote in a sweep group has care-order enrichment, the backend sends the
   * group from a single care order, so every quote in `quotes` has the same care order id.
   * That lets the row use the first quote as the representative source instead of scanning
   * the whole group.
   */
  get associatedCareOrder(): CareOrder | undefined {
    return this.quotes.at(0)?.careOrder;
  }

  /** The column id of which column this grouping level is grouping on. Ties this level of grouping to a column. */
  static getGroupColumnID(): string {
    return 'quote.SweepGroupID';
  }

  get groupColumnID() {
    return SweepGroupIDRow.getGroupColumnID();
  }

  /** A static function returning the data path component this class (level) represents */
  static getPathComponent(quote: Quote): string {
    return quote.SweepGroupID ?? SweepGroupIDRow.unassignedValue;
  }

  static getRowID(dataPath: string[]): string {
    invariant(dataPath.length > 0, 'Unable to call SweepGroupIDRow.getRowID with dataPath parameter of length 0');
    return dataPath.at(-1)!;
  }

  get rowID() {
    return SweepGroupIDRow.getRowID(this.dataPath);
  }

  groupColumnValueGetter(params: ValueGetterParams<SweepGroupIDRow>) {
    return this.SweepGroupID;
  }

  groupColumnValueFormatter({ value }: ValueFormatterParams<SweepGroupIDRow, string>): string {
    return value ?? '';
  }

  groupColumnFilterValueGetter(params: ValueGetterParams<SweepGroupIDRow>) {
    return this.groupColumnValueGetter(params);
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<SweepGroupIDRow, string>) {
    const cellRendererFunc = getCellRendererFunc(params.api, SweepGroupIDRow.getGroupColumnID());

    if (cellRendererFunc) {
      return cellRendererFunc(params);
    }

    return params.valueFormatted ?? '';
  }
}

export class ParentRFQIDRow implements TreeRow {
  dataPath: string[];
  ParentRFQID: string;
  SubmitTime?: string; // just needs to exist here for typing purposes on QuotesBlotterRow (its the default sorting key)
  Side = null; // Setting side to null tells the column that we should not render anything

  constructor(dataPath: string[], quote: Quote) {
    this.dataPath = dataPath;
    this.ParentRFQID = quote.ParentRFQID ?? UNASSIGNED_PARENT_RFQ_ID;
    this.SubmitTime = quote.careOrder?.SubmitTime;
  }

  /** The column id of which column this grouping level is grouping on. Ties this level of grouping to a column. */
  static getGroupColumnID(): string {
    return 'quote.ParentRFQID';
  }

  get groupColumnID() {
    return ParentRFQIDRow.getGroupColumnID();
  }

  /** A static function returning the data path component this class (level) represents */
  static getPathComponent(quote: Quote): string {
    return quote.ParentRFQID ?? UNASSIGNED_PARENT_RFQ_ID;
  }

  static getRowID(dataPath: string[]): string {
    invariant(dataPath.length > 0, 'Unable to call ParentRFQIDRow.getRowID with dataPath parameter of length 0');
    return dataPath.at(-1)!;
  }

  get rowID() {
    return ParentRFQIDRow.getRowID(this.dataPath);
  }

  groupColumnValueGetter(params: ValueGetterParams<ParentRFQIDRow>) {
    return this.ParentRFQID;
  }

  groupColumnValueFormatter({ value }: ValueFormatterParams<ParentRFQIDRow, string>): string {
    return value ?? '';
  }

  groupColumnFilterValueGetter(params: ValueGetterParams<ParentRFQIDRow>) {
    return this.groupColumnValueGetter(params);
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<ParentRFQIDRow, string>) {
    const cellRendererFunc = getCellRendererFunc(params.api, ParentRFQIDRow.getGroupColumnID());

    if (cellRendererFunc) {
      return cellRendererFunc(params);
    }

    return params.valueFormatted ?? '';
  }
}

export class InitiatingFromRow implements TreeRow {
  dataPath: string[];
  InitiatingFrom: string;
  SubmitTime?: string; // just needs to exist here for typing purposes on QuotesBlotterRow (its the default sorting key)
  Side = null; // Setting side to null tells the column that we should not render anything

  constructor(dataPath: string[], quote: Quote) {
    this.dataPath = dataPath;
    this.InitiatingFrom = quote.careOrder?.initiatingFrom ?? UNASSIGNED_INITIATING_FROM;
  }

  /** The column id of which column this grouping level is grouping on. Ties this level of grouping to a column. */
  static getGroupColumnID(): string {
    return 'InitiatingFrom';
  }

  get groupColumnID() {
    return InitiatingFromRow.getGroupColumnID();
  }

  /** A static function returning the data path component this class (level) represents */
  static getPathComponent(quote: Quote): string {
    return quote.careOrder?.initiatingFrom ?? UNASSIGNED_INITIATING_FROM;
  }

  static getRowID(dataPath: string[]): string {
    invariant(dataPath.length > 0, 'Unable to call InitiatingFromRow.getRowID with dataPath parameter of length 0');
    return dataPath.at(-1)!;
  }

  get rowID() {
    return InitiatingFromRow.getRowID(this.dataPath);
  }

  groupColumnValueGetter(params: ValueGetterParams<InitiatingFromRow>) {
    return this.InitiatingFrom;
  }

  groupColumnValueFormatter({ value }: ValueFormatterParams<InitiatingFromRow, string>): string {
    return value ?? '';
  }

  groupColumnFilterValueGetter(params: ValueGetterParams<InitiatingFromRow>) {
    return this.groupColumnValueGetter(params);
  }
}
